// One-shot DatoCMS export (2026-07-19) — hand-maintained repo source since.
// Migrated CMS copy (GEO master plan §3.5). "StartupCraft Inc" scrubbed per
// D2b. Copy edits now happen here, in-repo, with owner approval.

import type { AllFaqPagesData, FaqContentItem } from 'Data/types'

// Same shape the old AllFaqPagesQuery returned (11 categories, raw markdown
// answers) — FaqSection, the legacy FAQ page and getFaqPageMarkup consume it
// unchanged.
export const ALL_FAQ_PAGES_DATA: AllFaqPagesData = {
  allFaqPages: [
    {
      id: '23368330',
      order: 2,
      title: 'Clients',
      content: [
        {
          id: '23368327',
          question: 'What types of clients will StartupCraft serve?',
          answer:
            'We work with startups, SMBs, and enterprises of all sizes. Our process is structured enough for large organizations yet flexible enough for early-stage ventures.',
        },
        {
          id: '23368328',
          question: 'Can you show some of your clients?',
          answer:
            'Yes – please check our [portfolio](https://startupcraft.io/work) for case studies of our work.',
        },
        {
          id: '23368329',
          question: 'How can my project be featured in your portfolio?',
          answer:
            'We’re happy to showcase your project after completion. Our site attracts tens of thousands of visitors monthly, so featuring your product gives it additional visibility.',
        },
      ],
    },
    {
      id: '23368249',
      order: 5,
      title: 'Engineering',
      content: [
        {
          id: '23368246',
          question: 'What is the experience of StartupCraft engineers?',
          answer:
            'Our engineers go through a rigorous multi-stage selection process and continuous training on proprietary frameworks and modern tech. Many contribute to open source and internal R&D, ensuring you get cutting-edge expertise.\n',
        },
        {
          id: '23368247',
          question: 'Who are the development managers?',
          answer:
            'Each project is managed by both a project manager and a technical lead, ensuring precise planning, quality, and timely delivery.\n',
        },
        {
          id: '23368248',
          question:
            'Do you offer a dedicated team that works exclusively on my project?',
          answer:
            'Yes. We can allocate a dedicated team at full-retainer rates if your project requires undivided focus.',
        },
      ],
    },
    {
      id: '23368254',
      order: 10,
      title: 'Testing',
      content: [
        {
          id: '23368253',
          question: 'Does StartupCraft have testers? How do you test software?',
          answer:
            'Yes. Every team includes QA. We perform manual testing on every milestone and layer in automated tests for critical flows. Full 100% automated coverage is not cost-efficient, so we balance automation with practicality.',
        },
      ],
    },
    {
      id: '23368352',
      order: 9,
      title: 'Maintenance',
      content: [
        {
          id: '23368349',
          question:
            'How does support work after the initial development stage?',
          answer:
            'We provide priority access to the engineers who built your project. Support can be structured as a retainer (dedicated hours per month) or flexible hourly packages. We tailor it to your pace and needs.\n',
        },
        {
          id: '23368350',
          question: 'How do you treat bugs and issues after launch?',
          answer:
            'If it’s a bug within the agreed scope, we fix it free of charge. New changes or improvements are billed as additional work.\n',
        },
        {
          id: '23368351',
          question: 'What is a change request?',
          answer:
            'Any modification outside the signed scope. In fixed-price contracts, it triggers a re-evaluation of costs and timeline. In T&M, it’s simply added to the workload.',
        },
      ],
    },
    {
      id: '22260378',
      order: 8,
      title: 'General',
      content: [
        {
          id: '22260377',
          question: 'What are the advantages of working with StartupCraft?',
          answer:
            '* Based in **Sydney, Australia (Pty Ltd)**\n* Elite engineers in Australia and Europe\n* Full-stack services: AI, web, mobile, design, DevOps, QA\n* Transparent fixed-price contracts\n* Priority support after delivery\n* Legal protection under Australian law\n* Use of modern tech stacks proven by leading companies',
        },
        {
          id: '22260392',
          question: 'Can you work with companies from different time zones?',
          answer:
            'Yes. We align schedules, maintain overlapping hours, and use async tools like Slack and Jira to keep communication seamless worldwide.',
        },
        {
          id: 'IxJohQEOR-Weji_2MPce3Q',
          question: 'What if my executive team doesn’t want me to outsource?',
          answer:
            'Outsourcing with us lets you validate ideas, reduce overhead, and focus on your core business while leveraging our top-tier engineering team. Many clients later transition in-house after using us to reach product-market fit.',
        },
      ],
    },
    {
      id: '22260395',
      order: 7,
      title: 'Services',
      content: [
        {
          id: '22260394',
          question: 'What if I only have an idea and no specifications yet?',
          answer:
            'We run a **Blueprint phase** (Discovery). Deliverables include prototypes, roadmap, and detailed estimates. Cost: **$3k–$10k**, timeline: 2 weeks.\n',
        },
        {
          id: '23368152',
          question:
            'I already have a design — can you start from app development?',
          answer:
            'We always apply a minimum adjustment from our design team to make it Figma-compliant and developer-ready. This ensures smooth implementation.',
        },
        {
          id: '23368153',
          question: 'What services can I receive from StartupCraft?',
          answer:
            'Our focus is on:\n* **AI-driven applications**\n* **Web apps** (React, Node.js)\n* **Mobile apps** (React Native, cross-platform iOS & Android)\n* **UI/UX design & prototyping**\n* **Technical audits & CTO consulting**\n\n*Blockchain is no longer our main offering. We occasionally accept blockchain projects, but our priority is AI, web, and mobile.*',
        },
        {
          id: '23368171',
          question: 'Can StartupCraft build my app from scratch?',
          answer:
            'Yes — from team setup to launch, we provide full end-to-end development.',
        },
        {
          id: '23368172',
          question: 'Can StartupCraft improve or migrate my existing app?',
          answer:
            'Yes — we handle audits, feature upgrades, redesigns, migrations, and project rescues.',
        },
        {
          id: '23368173',
          question: 'Can StartupCraft build my mobile app?',
          answer:
            'Yes — we deliver cross-platform apps with React Native, published on iOS and Android.',
        },
        {
          id: '23368174',
          question: 'Can StartupCraft create a full-stack responsive web app?',
          answer:
            'Yes — we specialize in React/Node.js full-stack builds with modern practices like GraphQL and SSR.',
        },
      ],
    },
    {
      id: '23368237',
      order: 6,
      title: 'Cost',
      content: [
        {
          id: '23368229',
          question: 'How much will my project cost?',
          answer:
            'Typical ranges (in AUD):\n* Small projects: **$30k–$60k**\n* Mid-sized: **$60k–$150k**\n* Enterprise: **$150k+**\n\nFinal cost depends on scope and deadlines. We work under fixed-price contracts with milestone-based payments.',
        },
        {
          id: '23368230',
          question: 'Can I hire StartupCraft engineers in hourly mode?',
          answer:
            'Yes, but only under a structured minimum: 80 hours per month for at least 3 months. This ensures continuity and efficiency.\n',
        },
        {
          id: '23368231',
          question: 'The cost is outside of my budget. Is it worth it?',
          answer:
            'Yes. You can cut costs with cheaper vendors, but quality, stability, and long-term ROI suffer. Our work is validated by [client testimonials](https://clutch.co/profile/startupcraft) and consistent top ratings.',
        },
        {
          id: '23368232',
          question: 'If I don’t have enough budget, can we work for equity?',
          answer:
            'No. We don’t exchange services for equity. Instead, we recommend starting small through phased development.',
        },
        {
          id: '23368233',
          question: 'Why can’t I receive an estimate instantly?',
          answer:
            'Because we provide accurate quotes, not guesses. Our analysts and engineers review your scope in detail before committing. This takes 1-2 business days.\n',
        },
        {
          id: '23368234',
          question: 'How can we work under a fixed-price contract?',
          answer:
            'We define phases/milestones with fixed deliverables and costs. Once agreed, we deliver exactly what’s in scope for the agreed price — no hidden changes.',
        },
        {
          id: '23368236',
          question:
            'Do you provide maintenance and ongoing support after initial development?',
          answer:
            'Yes. We offer retained support packages or on-demand maintenance. Options range from dedicated engineers to hourly blocks, depending on your needs.',
        },
      ],
    },
    {
      id: '23368293',
      order: 4,
      title: 'Design',
      content: [
        {
          id: '23368291',
          question: 'Can you work with my design?',
          answer:
            'Yes — but our in-house designers will always apply grid systems and modern practices to ensure the design is implementation-ready. This prevents developer bottlenecks and avoids costly fixes later.\n',
        },
        {
          id: '23368292',
          question: 'Do you offer design services?',
          answer:
            'Yes. Our design team helps you shape your product vision with modern, scalable design practices for mobile, web, and landing pages.',
        },
      ],
    },
    {
      id: '23368301',
      order: 3,
      title: 'Process',
      content: [
        {
          id: '23368298',
          question: 'How can I start a project?',
          answer:
            'The process begins with the **Blueprint phase**. You can reach out through the integrated widget on our landing pages — just click and say hello. From there, we’ll guide you through scoping, estimates, and planning.',
        },
        {
          id: '23368299',
          question: 'Will I have to manage the engineers?',
          answer:
            'No. We provide project management and technical leadership. You’ll have a project manager and technical lead who handle architecture reviews, time allocation, and planning.',
        },
        {
          id: '23368300',
          question: 'What happens when my project ends?',
          answer:
            'Our involvement can end with delivery and documentation, or we can continue under a maintenance contract if you need ongoing development.',
        },
      ],
    },
    {
      id: '23368341',
      order: 1,
      title: 'Payment',
      content: [
        {
          id: '23368340',
          question: 'What payment options do you accept?',
          answer:
            'We accept wire/BSB/ACH transfers only. We do not work with PayPal, credit cards, checks, or cryptocurrency. Wire transfer is the most reliable and secure option for our clients and ensures funds are processed quickly without unnecessary fees.',
        },
      ],
    },
    {
      id: '23368381',
      order: 0,
      title: 'Confidentiality',
      content: [
        {
          id: '23368379',
          question:
            'Do I own the legal rights to technology built by StartupCraft?',
          answer:
            'You own all custom software we build for you, outright — source included, with no ongoing licence fee and no clause tying it to us.\n\nWhat stays ours is the internal framework code that existed before your project started: the 20,000+ engineering hours of shared infrastructure we bring to every build so you are not paying to have solved problems solved again. You get a perpetual, transferable licence to use that framework code inside the product we deliver — including if you later take the codebase to another firm or in-house team.\n\nThe one restriction: you cannot extract those framework components and resell them as a standalone product. Nothing else you would normally want to do with your own software is affected.\n',
        },
        {
          id: 'WcGfomT7T_OWKCaFolftwA',
          question: 'Will you sign an NDA?',
          answer:
            'Absolutely. We sign a mutual Non-Disclosure Agreement (NDA) with every client before starting a project. Additionally, every StartupCraft employee, contractor, and team member is bound by a strict NDA as part of our company policy. Your confidential information and intellectual property are fully protected.',
        },
        {
          id: '23368380',
          question: 'How can I be sure my project is kept confidential?',
          answer:
            'We enforce confidentiality at all levels. Only the specific team assigned to your project will know the details. Every member of StartupCraft – employees, contractors, and partners – is under NDA, ensuring your intellectual property remains protected. We also implement strict security practices overseen by our founders. StartupCraft is a Sydney-based Pty Ltd company, fully governed by Australian corporate and privacy laws, so your contract and IP are safeguarded under AU law.',
        },
      ],
    },
  ],
}

// Rendered always-open at the top of /faq as heading + answer pairs. These are
// the strings buyers actually type, and question-heading-then-direct-answer is
// the unit an engine can lift; anything nested two collapses deep is not.
export const KEY_FAQ_ITEMS: FaqContentItem[] = [
  {
    id: 'key-cost-australia',
    question: 'How much does custom software cost in Australia?',
    answer:
      'Build engagements with us start at $25,000 AUD and scale with scope — the number of systems involved moves it far more than the number of screens. A paid discovery gives you a costed architecture before you commit to any of it. If what you need is genuinely served by off-the-shelf software, we will tell you that instead.',
  },
  {
    id: 'key-paid-discovery',
    question: 'What is a paid discovery and why do you charge for it?',
    answer:
      'It is two weeks of senior engineering time spent mapping how your operation actually works, ending in a written architecture, a build sequence and an honest read on what not to build. It starts at $3,000 AUD. We charge for it because free scoping gets free-quality attention, and because the document is worth having whether or not we are the ones who build — you keep it either way.',
  },
  {
    id: 'key-automate-reporting',
    question: 'Can you automate our reporting process?',
    answer:
      'Usually, yes. Reporting is the most commonly automatable thing we find, because the numbers almost always already exist in systems that do not talk to each other — so the work is normally integration rather than AI. Discovery tells you which parts are worth automating and which are cheaper left alone.',
  },
  {
    id: 'key-own-the-code',
    question: 'Do we own the code you write?',
    answer:
      'You own every line of custom software we build for you, outright, source included. We keep ownership of the internal frameworks we bring to the project — the ones that predate your engagement — and you get a perpetual, transferable licence to use them inside what we deliver. The full split is under Confidentiality below.',
  },
  {
    id: 'key-how-long',
    question: 'How long does a custom build take?',
    answer:
      'It depends almost entirely on how many existing systems the build has to talk to. Integrations, not the build itself, are the usual source of delay. Discovery produces a sequence with those dependencies identified up front, so you get a timeline you can plan against rather than a guess.',
  },
  {
    id: 'key-outside-australia',
    question: 'Do you work with businesses outside Australia?',
    answer:
      'Yes. We are headquartered in Sydney and our engineers work across European and APAC time zones, so we cover most of the working day either side. We have delivered for clients in North America, Europe, the Middle East and Australia.',
  },
]
